import { useEffect, useState, useContext } from 'react';
import ReactDatePicker from "react-datepicker";
import { useNavigate, useParams } from 'react-router-dom';
import Select from "react-select";
import {
  getBeatsApi,
  getCustomersApi,
  getVouchersApi,
  saveReceiptApi,
  getReceiptDetailApi,
  updateReceiptDetailApi
} from '../api/api';
import { AuthContext } from '../context/AuthContext.jsx';
import { useOrganizationSettings } from '../context/OrganizationSettingsContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import './ReceiptEditForm.css';

export default function ReceiptEditForm() {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const { settings } = useOrganizationSettings();
  const navigate = useNavigate();
  const [beats, setBeats] = useState([]);
  const [selectedBeat, setSelectedBeat] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedReceiver, setSelectedReceiver] = useState('');
  const [vouchers, setVouchers] = useState([]);
  const [totalBillAmount, setTotalBillAmount] = useState(0);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);
  const [totalRemainingAmount, setTotalRemainingAmount] = useState(0);
  const [chequeDate, setChequeDate] = useState(null);
  const [upiDate, setUpiDate] = useState(null);
  const [receiptDate, setReceiptDate] = useState(new Date());
  const [upiFileImage, setUpiFile] = useState(null);
  const [receiptDetail, setReceiptDetail] = useState({});

  const [form, setForm] = useState({
    receiptDate: new Date().toISOString().slice(0, 10),
    tallyCustomerId: null,
    cashAmount: null,
    upiAmount: null,
    chequeAmount: null,
    upiNumber: null,
    upiDate: null,
    chequeNumber: null,
    chequeDate: null,
    bankName: null,
    remark: null,
    advanceAmount: null,
    // billAllocations entries:
    // { id: <allocationId|null>, receiptId: <receiptId|null>, voucherId: <voucherRowId|null>, billNumber, billPaidAmount, billData }
    billAllocations: [],
    billErrors: [],
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (msg) {
      const timer = setTimeout(() => {
        setMsg('');
      }, 3000); // 3 seconds
      return () => clearTimeout(timer);
    }
  }, [msg]);

  // disable/clear UPI fields when upiAmount is empty or zero
    useEffect(() => {
      const enabled = parseFloat(form.upiAmount || 0) > 0;
      if (!enabled) {
        // clear UPI related values (form + local state)
        setForm(prev => ({ ...prev, upiNumber: '', upiDate: null }));
        setUpiDate(null);
        setUpiFile(null);
      }
    }, [form.upiAmount]);
    
    // disable/clear Cheque fields when chequeAmount is empty or zero
    useEffect(() => {
      const enabled = parseFloat(form.chequeAmount || 0) > 0;
      if (!enabled) {
        // clear UPI related values (form + local state)
        setForm(prev => ({ ...prev, chequeNumber: '', chequeDate: null }));
        setChequeDate(null);
      }
  }, [form.chequeAmount]);

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]); // Get only base64 part
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });
  }

  // Safe number helpers
  const toNumber = (val) => {
    const n = parseFloat(val);
    return isNaN(n) ? 0 : n;
  };
  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

  // Load beats
  useEffect(() => {
    getBeatsApi()
      .then((res) => setBeats(res.data?.data || []))
      .catch(() => setBeats([]));
  }, []);

  // Load customers when beat changes
  useEffect(() => {
    if (!selectedBeat) { setCustomers([]); return; }
    getCustomersApi({ beatName: selectedBeat })
      .then(res => setCustomers(res.data?.data || []))
      .catch(() => setCustomers([]));
  }, [selectedBeat]);

  // Load receipt detail for editing
  useEffect(() => {
    if (!id) {
      navigate('/receipts/list');
      return;
    }
    getReceiptDetailApi({ receiptId: id })
      .then(res => {
        const data = res.data?.data;
        if (!data) return;

        // put server allocations into form (no voucherId yet — will merge after vouchers load)
        setForm(prev => ({
          ...prev,
          receiptDate: data.receiptDate ? data.receiptDate.slice(0, 10) : new Date().toISOString().slice(0, 10),
          tallyCustomerId: data.tallyCustomerId || null,
          cashAmount: data.cashAmount ?? '',
          upiAmount: data.upiAmount ?? '',
          chequeAmount: data.chequeAmount ?? '',
          upiNumber: data.upiNumber ?? '',
          chequeNumber: data.chequeNumber ?? '',
          bankName: data.bankName ?? '',
          remark: data.remark ?? '',
          advanceAmount: data.advanceAmount ?? '',
          billAllocations: (data.ReceiptAllocations || []).map(a => ({
            id: a.id,
            receiptId: a.receiptId,
            voucherId: null, // will be filled when vouchers load (matching by billNumber)
            billNumber: a.billNumber,
            billPaidAmount: a.billPaidAmount,
            billData: {
              billAmount: a.billAmount,
              billDate: a.billDate,
              billDueDate: a.billDueDate,
              isApproved: a.isApproved
            }
          })),
        }));

        setSelectedBeat(data.beatName || '');
        setSelectedCustomer(data.customerName || '');
        if (data.receiptDate) setReceiptDate(new Date(data.receiptDate));
        if (data.upiDate) setUpiDate(data.upiDate ? new Date(data.upiDate) : null);
        if (data.chequeDate) setChequeDate(data.chequeDate ? new Date(data.chequeDate) : null);

      })
      .catch(() => setReceiptDetail({}));
  }, [id, navigate]);

  // Load vouchers for selected beat+customer and merge allocations
  useEffect(() => {
    if (!selectedCustomer) {
      setVouchers([]);
      setTotalBillAmount(0);
      setTotalPaidAmount(0);
      setTotalRemainingAmount(0);
      // When customer changed, clear allocations so old allocations don't stick
      setForm(prev => ({ ...prev, billAllocations: [] }));
      return;
    }
    setLoading(true);
    const params = { customerName: selectedCustomer };
    if (settings.beatWise && selectedBeat) {
      params.beatName = selectedBeat;
    }
    getVouchersApi(params)
      .then((res) => {
        const voucherRows = res.data?.data || [];
        setVouchers(voucherRows);
        setTotalBillAmount(res.data?.totalBillAmount || 0);
        setTotalPaidAmount(res.data?.totalPaidAmount || 0);
        setTotalRemainingAmount(res.data?.totalRemainingAmount || 0);

        // Merge existing allocations (from form) with vouchers
        setForm(prev => {
          const existing = prev.billAllocations || [];

          // For each voucher, try to find an existing allocation by billNumber (or voucherId if previously set)
          const merged = voucherRows.map(v => {
            const match = existing.find(a => (
              (a.voucherId && a.voucherId === v.id) || // exact voucher match
              (a.billNumber && a.billNumber === v.billNumber) // fallback by billNumber
            ));
            return {
              id: match?.id || null,
              receiptId: match?.receiptId || id || null,
              voucherId: v.id,
              billNumber: v.billNumber,
              // show allocation amount if existing allocation exists, otherwise show voucher.paidAmount if any, else ''
              billPaidAmount: (match && (match.billPaidAmount !== undefined && match.billPaidAmount !== null))
                ? match.billPaidAmount
                : (v.paidAmount != null ? v.paidAmount : ''),
              billData: {
                billAmount: v.billAmount,
                billDate: v.billDate,
                billDueDate: v.billDueDate,
                // merged any existing extra fields
                ...(match?.billData || {})
              }
            };
          });

          // keep any existing allocations that aren't present in current vouchers (append them)
          const unmatched = existing.filter(a => !merged.some(m => m.billNumber === a.billNumber));
          return { ...prev, billAllocations: [...merged, ...unmatched] };
        });

      })
      .catch(() => {
        setVouchers([]);
        setTotalBillAmount(0);
        setTotalPaidAmount(0);
        setTotalRemainingAmount(0);
      })
      .finally(() => setLoading(false));
  }, [selectedBeat, selectedCustomer, id, settings.beatWise]);

  const handleBeatChange = e => {
    setSelectedBeat(e.target.value);
    setSelectedCustomer('');
    // clear vouchers and allocations
    setVouchers([]);
    setTotalBillAmount(0);
    setTotalPaidAmount(0);
    setTotalRemainingAmount(0);
    setForm(prev => ({ ...prev, billAllocations: [] }));
    handleRemark(e.target.value);
  };

  const handleReceiverChange = e => {
    setSelectedReceiver(e.target.value);
  };

  const handleCustomerChange = e => {
    setSelectedCustomer(e.target.value);
    // clear vouchers and allocations
    setVouchers([]);
    setTotalBillAmount(0);
    setTotalPaidAmount(0);
    setTotalRemainingAmount(0);
    setForm(prev => ({ ...prev, billAllocations: [] }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleOnlyNumber = (e) => {
    if (
      [8, 9, 27, 13, 46, 110, 190].includes(e.keyCode) ||
      (e.key >= '0' && e.key <= '9') ||
      ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key) ||
      (e.key === '-' && e.target.selectionStart === 0)
    ) {
      // allow
    } else {
      e.preventDefault();
    }
  };

  // Update or create allocation by voucherId (UI uses voucherId). Fallback: match by billNumber.
  const handleBillAllocations = (voucherId, billNumber, value, billData) => {
    let errorMsg = "";
    const balance = billData.balance ?? billData.remainingAmount ?? 0;
    if (value && parseFloat(value) > parseFloat(balance)) {
      errorMsg = "Allocation amount cannot exceed the available balance";
    }

    setForm(prev => {
      const existing = prev.billAllocations || [];
      const errors = prev.billErrors || {};
      const idx = existing.findIndex(item =>
        (item.voucherId && item.voucherId === voucherId) || (item.billNumber && item.billNumber === billNumber)
      );

      let newAllocations;
      if (idx === -1) {
        newAllocations = [
          ...existing,
          {
            id: null,
            receiptId: id || null,
            voucherId: voucherId || null,
            billNumber,
            billPaidAmount: value,
            billData: billData || {}
          }
        ];
      } else {
        newAllocations = [...existing];
        newAllocations[idx] = {
          ...newAllocations[idx],
          voucherId: voucherId || newAllocations[idx].voucherId || null,
          billPaidAmount: value,
          billData: billData || newAllocations[idx].billData || {}
        };
      }
      // Update errors for this bill
      const newErrors = { ...errors, [billNumber]: errorMsg };

      return { ...prev, billAllocations: newAllocations, billErrors: newErrors };
    });
  };

  // read allocation value for a voucher row
  const getAllocationValue = (voucherId, billNumber) => {
    const item = (form.billAllocations || []).find(i =>
      (i.voucherId && i.voucherId === voucherId) || (i.billNumber && i.billNumber === billNumber)
    );
    return item ? item.billPaidAmount : '';
  };

  const handleAdvanceAmount = () =>{
    const totalAllocated = form.billAllocations.reduce(
      (sum, item) => sum + (parseFloat(item.billPaidAmount) || 0),
      0
    );
    // Calculate total payment entered
    const cash = parseFloat(form.cashAmount) || 0;
    const upi = parseFloat(form.upiAmount) || 0;
    const cheque = parseFloat(form.chequeAmount) || 0;
    const totalPaid = cash + upi + cheque;
    // console.log("totalPaid", totalPaid)
    let advanceAmount = totalPaid - totalAllocated;
    // let advanceAmount = totalPaid - totalRemainingAmount;
    if(advanceAmount < 0) advanceAmount = null;
    setForm(prev => ({
      ...prev,
      advanceAmount
    }));
  }

  const handleRemark = (beat) =>{
    let remarkText = `${beat}, ${user?.userName || user?.name || 'User'}, ${form.receiptDate}`;
    setForm(prev => ({
      ...prev,
      remark: remarkText
    }));
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');

    // Basic validation: at least one payment amount
    if (
      (!form.cashAmount || toNumber(form.cashAmount) === 0) &&
      (!form.upiAmount || toNumber(form.upiAmount) === 0) &&
      (!form.chequeAmount || toNumber(form.chequeAmount) === 0)
    ) {
      setMsg('Please enter at least one payment amount (Cash, UPI, or Cheque).');
      setSaving(false);
      return;
    }
    if (form.upiAmount && (!form.upiNumber || !upiDate)) {
      setMsg('Please provide UPI Number and UPI Date for UPI payments.');
      setSaving(false);
      return;
    }
    if (form.chequeAmount && (!form.chequeNumber || !chequeDate)) {
      setMsg('Please provide Cheque Number and Cheque Date for Cheque payments.');
      setSaving(false);
      return;
    }

    // Check if there are any bill allocation errors
    const hasBillErrors = Object.values(form.billErrors || {}).some(err => err && err.trim() !== '');
    if (hasBillErrors) {
      setMsg('Please adjust the bill allocation amounts before submitting.');
      setSaving(false);
      return;
    }

    // const hasBillErrors = Object.values(form.billErrors).some(err => err);
    // console.log("form.hasBillErrors", hasBillErrors);
    // if (hasBillErrors) {
    //   setMsg('Please adjust the bill allocation amounts before submitting ggg.');
    //   setSaving(false);
    //   return;
    // }

    // Calculate total entered in bill allocations
    const totalAllocated = form.billAllocations.reduce(
      (sum, item) => sum + (parseFloat(item.billPaidAmount) || 0),
      0
    );
    
    // Calculate total payment entered
    const cash = parseFloat(form.cashAmount) || 0;
    const upi = parseFloat(form.upiAmount) || 0;
    const cheque = parseFloat(form.chequeAmount) || 0;
    const advanceAmount = form.advanceAmount ? parseFloat(form.advanceAmount) : 0;
    const totalPaid = cash + upi + cheque;
    const totalAllocatedWithAdvance = totalAllocated + advanceAmount;

    // Validation: amounts must match
    if (totalAllocatedWithAdvance !== totalPaid) {
      setMsg('Total bill allocation amount and advance amount must match the sum of Cash, UPI, and Cheque amounts.');
      setSaving(false);
      return;
    }
    
    const checkAdvanceAmount = Math.sign(advanceAmount);
    if (checkAdvanceAmount === -1) {
      setMsg('Advance amount should not be in negative. Please adjust the bill allocation amounts.');
      setSaving(false);
      return;
    }
    
    // Use small tolerance to avoid float issues
    const tolerance = 0.01;
    // debug
    // // console.log("Allocated:", totalAllocated, "Paid:", totalPaid);
    // if (Math.abs(allocated - totalPaid) > tolerance) {
    //   setMsg('Total bill allocation amount must match the sum of Cash, UPI, and Cheque amounts.');
    //   setSaving(false);
    //   return;
    // }

    try {
      let upiFileBase64 = null;
      if (upiFileImage) {
        upiFileBase64 = await fileToBase64(upiFileImage);
      }

      // prepare allocations: only include allocations that have amount > 0
      const payloadAllocations = (form.billAllocations || [])
        .map(a => ({
          id: a.id || null,
          receiptId: a.receiptId || id || null,
          voucherId: a.voucherId || null,
          billNumber: a.billNumber,
          billPaidAmount: toNumber(a.billPaidAmount),
          billData: a.billData || {}
        }))
        .filter(a => a.billPaidAmount > 0);

      const payload = {
        ...form,
        receiptId: id,
        beatName: settings.beatWise ? selectedBeat : null,
        customerName: selectedCustomer,
        receiver: selectedReceiver,
        chequeDate: chequeDate ? chequeDate.toISOString().slice(0, 10) : null,
        upiDate: upiDate ? upiDate.toISOString().slice(0, 10) : null,
        receiptDate: receiptDate.toISOString().slice(0, 10),
        upiFileImage: upiFileBase64,
        upiFileName: upiFileImage ? upiFileImage.name : null,
        billAllocations: settings.billWise ? payloadAllocations : [],
      };

      // Update (edit) or Save (create)
      if (id) {
        await updateReceiptDetailApi(payload);
        setMsg("Receipt updated successfully.");
      } else {
        await saveReceiptApi(payload);
        setMsg("Receipt saved successfully.");
      }

      // Reset minimal fields & redirect
      setForm(f => ({
        ...f,
        customerName: '',
        cashAmount: '',
        upiAmount: '',
        upiNumber: '',
        upiDate: '',
        chequeAmount: '',
        chequeNumber: '',
        chequeDate: '',
        bankName: '',
        remark: '',
        advanceAmount: '',
        billAllocations: []
      }));

      navigate('/receipts/list');
    } catch (err) {
      console.log(err);
      setMsg(id ? 'Failed to update Receipt.' : 'Failed to save Receipt.');
    } finally {
      setSaving(false);
    }
  };

  const receiverOptions = [
    { value: 'Supply 1', label: 'Supply 1' },
    { value: 'Supply 2', label: 'Supply 2' },
    { value: 'Supply 3', label: 'Supply 3' },
    { value: 'Supply 4', label: 'Supply 4' },
    { value: 'Supply 5', label: 'Supply 5' },
  ];

  return (
    <div className="receipt-form-container">
      <div className="receipt-form-card">
        <form onSubmit={handleSubmit}>
          <div className="receipt-form-header">
            <h4>{id ? 'Edit Receipt' : 'Receipt Entry'}</h4>
          </div>

          {/* Basic Information Section */}
          <div className="receipt-form-section">
            <div className="receipt-form-section-title">Basic Information</div>
            <div className="receipt-form-grid">
              <FormField label="Date" id="receiptDate">
                <ReactDatePicker
                  selected={receiptDate}
                  onChange={date => setReceiptDate(date)}
                  className="form-field-input"
                  dateFormat="dd-MM-yyyy"
                  placeholderText="Select a date"
                  disabled={true}
                  name="receiptDate"
                />
              </FormField>
              
              {user?.isOwner == true && (
                <FormField label="Receiver" id="receiver">
                  <Select
                    options={receiverOptions.map(b => ({ value: b.value, label: b.label }))}
                    value={selectedReceiver ? { value: selectedReceiver, label: selectedReceiver } : null}
                    onChange={(opt) => {
                      handleReceiverChange({ target: { value: opt ? opt.value : "" } });
                    }}
                    placeholder="Select Receiver"
                    isClearable
                    isSearchable
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </FormField>
              )}
              
              {settings.beatWise && (
                <FormField label="Beat" id="beat" required>
                  <Select
                    options={beats.map(b => ({ value: b.beatName, label: b.beatName }))}
                    value={selectedBeat ? { value: selectedBeat, label: selectedBeat } : null}
                    onChange={(opt) => {
                      handleBeatChange({ target: { value: opt ? opt.value : "" } });
                    }}
                    placeholder="Select Beat"
                    isClearable
                    isSearchable
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </FormField>
              )}
              
              <FormField label="Customer" id="customer" required>
                <Select
                  options={customers.map(c => ({ value: c.customerName, label: c.customerName }))}
                  value={selectedCustomer ? { value: selectedCustomer, label: selectedCustomer } : null}
                  onChange={(opt) => {
                    handleCustomerChange({ target: { value: opt ? opt.value : "" } });
                  }}
                  placeholder="Select Customer"
                  isClearable
                  isSearchable
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </FormField>
              
              <div className="receipt-form-grid-full">
                <div className="receipt-pending-badge">
                  <span className="receipt-pending-badge-label">Pending Amount:</span>
                  <span className="receipt-pending-badge-value">₹ {totalRemainingAmount ?? 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Information Section */}
          <div className="receipt-form-section">
            <div className="receipt-form-section-title">Payment Information</div>
            <div className="receipt-form-grid">
              <FormField
                label="Cash Amount"
                id="cashAmount"
                name="cashAmount"
                type="number"
                value={form.cashAmount}
                onChange={handleChange}
                placeholder="Enter cash amount"
                step="any"
                pattern="[0-9]*"
                onKeyUp={handleAdvanceAmount}
                onKeyDown={handleOnlyNumber}
              />
              
              <FormField
                label="UPI Amount"
                id="upiAmount"
                name="upiAmount"
                type="number"
                value={form.upiAmount || ''}
                onChange={handleChange}
                placeholder="Enter UPI amount"
                step="any"
                pattern="[0-9]*"
                onKeyUp={handleAdvanceAmount}
                onKeyDown={handleOnlyNumber}
              />
              
              <FormField
                label="UPI Number"
                id="upiNumber"
                name="upiNumber"
                type="text"
                value={form.upiNumber || ''}
                onChange={handleChange}
                placeholder="Enter UPI number"
                disabled={!(parseFloat(form.upiAmount || 0) > 0)}
              />
              
              <FormField label="UPI Date" id="upiDate">
                <ReactDatePicker
                  selected={upiDate}
                  onChange={date => setUpiDate(date)}
                  dateFormat="dd-MM-yyyy"
                  className="form-field-input"
                  placeholderText="Select UPI date"
                  disabled={!(parseFloat(form.upiAmount || 0) > 0)}
                />
              </FormField>
              
              <FormField
                label="UPI File"
                id="upiFileImage"
                name="upiFileImage"
                type="file"
                onChange={e => setUpiFile(e.target.files[0])}
                accept=".jpg,.jpeg,.png,.pdf"
                disabled={!(parseFloat(form.upiAmount || 0) > 0)}
              />
              
              <FormField
                label="Cheque Amount"
                id="chequeAmount"
                name="chequeAmount"
                type="number"
                value={form.chequeAmount || ''}
                onChange={handleChange}
                placeholder="Enter cheque amount"
                step="any"
                pattern="[0-9]*"
                onKeyDown={handleOnlyNumber}
                onKeyUp={handleAdvanceAmount}
              />
              
              <FormField
                label="Cheque Number"
                id="chequeNumber"
                name="chequeNumber"
                type="text"
                value={form.chequeNumber || ''}
                onChange={handleChange}
                placeholder="Enter cheque number"
                disabled={!(parseFloat(form.chequeAmount || 0) > 0)}
              />
              
              <FormField label="Cheque Date" id="chequeDate">
                <ReactDatePicker
                  selected={chequeDate}
                  onChange={date => setChequeDate(date)}
                  dateFormat="dd-MM-yyyy"
                  className="form-field-input"
                  placeholderText="Select cheque date"
                  disabled={!(parseFloat(form.chequeAmount || 0) > 0)}
                />
              </FormField>
              
              <FormField
                label="Advance Amount"
                id="advanceAmount"
                name="advanceAmount"
                type="number"
                value={form.advanceAmount || ''}
                onChange={handleChange}
                placeholder="Auto-calculated"
                step="any"
                pattern="[0-9]*"
                onKeyDown={handleOnlyNumber}
                readOnly
                helperText="This amount is automatically calculated"
              />
            </div>
          </div>

          {/* Pending Bills Section - Only show if billWise is enabled */}
          {settings.billWise && (
          <div className="receipt-form-section receipt-bills-section">
            <div className="receipt-bills-title">Pending Bills</div>
            <div className="receipt-bills-table-wrapper">
              <table className="receipt-bills-table">
                <thead>
                  <tr>
                    <th>Bill Information</th>
                    <th>Allocation Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={2} className="receipt-form-loading">
                        Loading bills...
                      </td>
                    </tr>
                  ) : vouchers.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="receipt-form-empty">
                        No pending bills found.
                      </td>
                    </tr>
                  ) : (
                    vouchers.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <div className="bill-info">
                            <span className="bill-number">{row.billNumber ?? '-'}</span>
                            <span className="bill-detail">
                              Date: {row.billDate ? row.billDate.slice(0, 10) : '-'}
                            </span>
                            <span className="bill-detail">
                              Balance: ₹ {row.remainingAmount ?? row.balance ?? '-'}
                            </span>
                          </div>
                        </td>
                        <td>
                          <input
                            type="number"
                            className={`bill-amount-input ${form.billErrors?.[row.billNumber] ? 'error' : ''}`}
                            placeholder="Enter amount"
                            value={getAllocationValue(row.id, row.billNumber) || ''}
                            min={0}
                            onChange={e =>
                              handleBillAllocations(
                                row.id,
                                row.billNumber,
                                e.target.value,
                                row
                              )
                            }
                            step="any"
                            onKeyDown={handleOnlyNumber}
                            onKeyUp={handleAdvanceAmount}
                            pattern="[0-9]*"
                          />
                          {form.billErrors?.[row.billNumber] && (
                            <div className="bill-error-message">
                              {form.billErrors[row.billNumber]}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          )}

          {/* Remarks Section */}
          <div className="receipt-form-section">
            <FormField label="Remark" id="remark" name="remark">
              <textarea
                className="form-field-input"
                placeholder="Enter any additional remarks"
                name="remark"
                value={form.remark || ''}
                onChange={handleChange}
                rows={3}
              />
            </FormField>
          </div>

          {/* Form Actions */}
          <div className="receipt-form-actions">
            <Button
              type="submit"
              variant="success"
              disabled={saving}
              loading={saving}
              className="receipt-form-submit-button"
            >
              {id ? 'Update Receipt' : 'Save Receipt'}
            </Button>
          </div>
          
          {msg && (
            <div className={`receipt-form-message ${msg.includes('successfully') ? 'success' : 'error'}`}>
              {msg}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
